import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { downvoteQuestion, upvoteQuestion } from '../../../services/questionService';
import useUserContext from '../../../hooks/useUserContext';
import { PopulatedDatabaseQuestion } from '../../../types/types';
import useVoteStatus from '../../../hooks/useVoteStatus';
import { Button } from '../../ui/button';
import cn from '../../../lib/utils';

/**
 * Interface represents the props for the VoteComponent.
 *
 * question - The question object containing voting information.
 */
interface VoteComponentProps {
  question: PopulatedDatabaseQuestion;
}

/**
 * A Vote component that allows users to upvote or downvote a question.
 *
 * @param question - The question object containing voting information.
 */
const VoteComponent = ({ question }: VoteComponentProps) => {
  const { user } = useUserContext();
  const { count, voted } = useVoteStatus({ question });

  /**
   * Function to handle upvoting or downvoting a question.
   *
   * @param type - The type of vote, either 'upvote' or 'downvote'.
   */
  const handleVote = async (type: string) => {
    try {
      if (question._id) {
        if (type === 'upvote') {
          await upvoteQuestion(question._id, user.username);
        } else if (type === 'downvote') {
          await downvoteQuestion(question._id, user.username);
        }
      }
    } catch (error) {
      // Handle error
    }
  };

  return (
    <div className='flex items-center gap-2 p-3'>
      <Button
        variant='outline'
        size='sm'
        className={cn(
          'flex items-center gap-1',
          voted === 1 && 'bg-green-100 border-green-500 text-green-700 hover:bg-green-200',
        )}
        onClick={() => handleVote('upvote')}>
        <ThumbsUp size={16} />
        <span>Upvote</span>
      </Button>

      <Button
        variant='outline'
        size='sm'
        className={cn(
          'flex items-center gap-1',
          voted === -1 && 'bg-red-100 border-red-500 text-red-700 hover:bg-red-200',
        )}
        onClick={() => handleVote('downvote')}>
        <ThumbsDown size={16} />
        <span>Downvote</span>
      </Button>

      <span className='ml-2 font-medium'>{count}</span>
    </div>
  );
};

export default VoteComponent;
